import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword, verifyResetToken } from '../config/api';
import { toast } from 'react-toastify';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [passwords, setPasswords] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    message: '',
    color: ''
  });

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    if (!token) {
      toast.error('Invalid or missing reset token');
      setVerifying(false);
      return;
    }

    setVerifying(true);
    try {
      const response = await verifyResetToken(token);
      
      if (response.success) {
        setTokenValid(true);
      } else {
        setTokenValid(false);
        toast.error(response.message || 'Invalid or expired reset token');
      }
    } catch (error) {
      console.error('Error verifying token:', error);
      setTokenValid(false);
      toast.error('Failed to verify reset token');
    } finally {
      setVerifying(false);
    }
  };

  const calculatePasswordStrength = (password) => {
    let score = 0;
    
    if (!password) {
      return { score: 0, message: '', color: '' };
    }

    // Length check
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    
    // Character variety checks
    if (/[a-z]/.test(password)) score++; // lowercase
    if (/[A-Z]/.test(password)) score++; // uppercase
    if (/[0-9]/.test(password)) score++; // numbers
    if (/[^a-zA-Z0-9]/.test(password)) score++; // special characters

    // Determine strength
    let message = '';
    let color = '';
    
    if (score <= 2) {
      message = 'Weak';
      color = '#dc3545';
    } else if (score <= 4) {
      message = 'Medium';
      color = '#ffc107';
    } else {
      message = 'Strong';
      color = '#198754';
    }

    return { score, message, color };
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPasswords({ ...passwords, newPassword });
    setPasswordStrength(calculatePasswordStrength(newPassword));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!passwords.newPassword || !passwords.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (passwords.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    // Check password strength
    if (passwordStrength.score < 3) {
      toast.warning('Please use a stronger password');
      return;
    }

    setLoading(true);

    try {
      const response = await resetPassword(token, passwords.newPassword);
      
      if (response.success) {
        toast.success('Password reset successfully! Redirecting to login...');
        setTimeout(() => {
          navigate('/login', { 
            state: { message: 'Password reset successful! Please login with your new password.' }
          });
        }, 2000);
      } else {
        toast.error(response.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ 
              fontSize: '48px', 
              marginBottom: '16px',
              animation: 'spin 1s linear infinite'
            }}>
              ⏳
            </div>
            <h3 style={{ color: '#495057', marginBottom: '8px' }}>Verifying Reset Link...</h3>
            <p style={{ color: '#6c757d', fontSize: '14px' }}>
              Please wait while we verify your password reset link
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ 
              fontSize: '48px', 
              marginBottom: '16px',
              color: '#dc3545'
            }}>
              ⚠️
            </div>
            <h2 className="login-title">Invalid or Expired Link</h2>
          </div>

          <div style={{ 
            padding: '16px', 
            backgroundColor: '#f8d7da', 
            border: '1px solid #f5c6cb', 
            borderRadius: '6px',
            marginBottom: '24px',
            color: '#721c24'
          }}>
            <p style={{ margin: '0 0 12px 0', fontWeight: '500' }}>
              This password reset link is invalid or has expired.
            </p>
            <p style={{ margin: '0', fontSize: '14px' }}>
              Password reset links expire after 1 hour for security reasons.
            </p>
          </div>

          <div style={{ 
            padding: '16px', 
            backgroundColor: '#e7f3ff', 
            border: '1px solid #b3d9ff', 
            borderRadius: '6px',
            marginBottom: '24px',
            fontSize: '14px',
            color: '#0056b3'
          }}>
            <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>
              💡 What to do next:
            </p>
            <ul style={{ margin: '0', paddingLeft: '20px' }}>
              <li>Request a new password reset link</li>
              <li>Check your email for the new link</li>
              <li>Complete the reset within 1 hour</li>
            </ul>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Link to="/forgot-password" className="btn btn-primary" style={{ width: '100%', textAlign: 'center' }}>
              Request New Reset Link
            </Link>
            
            <Link to="/login" className="btn btn-secondary" style={{ width: '100%', textAlign: 'center' }}>
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ 
            fontSize: '48px', 
            marginBottom: '16px',
            color: '#198754'
          }}>
            🔐
          </div>
          <h2 className="login-title">Reset Your Password</h2>
          <p style={{ color: '#6c757d', fontSize: '14px', marginTop: '8px' }}>
            Enter your new password below
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="newPassword" className="form-label">
              New Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                className="form-control"
                value={passwords.newPassword}
                onChange={handlePasswordChange}
                required
                placeholder="Enter new password"
                autoComplete="new-password"
                autoFocus
                style={{ paddingRight: '40px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px',
                  padding: '0',
                  color: '#6c757d'
                }}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            
            {passwords.newPassword && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '4px'
                }}>
                  <small style={{ fontSize: '12px', color: '#6c757d' }}>
                    Password Strength:
                  </small>
                  <small style={{ 
                    fontSize: '12px', 
                    fontWeight: '600',
                    color: passwordStrength.color 
                  }}>
                    {passwordStrength.message}
                  </small>
                </div>
                <div style={{ 
                  height: '4px', 
                  backgroundColor: '#e9ecef', 
                  borderRadius: '2px',
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    height: '100%', 
                    width: `${(passwordStrength.score / 6) * 100}%`,
                    backgroundColor: passwordStrength.color,
                    transition: 'all 0.3s ease'
                  }} />
                </div>
              </div>
            )}
            
            <small style={{ color: '#6c757d', fontSize: '12px', display: 'block', marginTop: '6px' }}>
              Must be at least 8 characters with uppercase, lowercase, numbers, and special characters
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirm New Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                className="form-control"
                value={passwords.confirmPassword}
                onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                required
                placeholder="Confirm new password"
                autoComplete="new-password"
                style={{ paddingRight: '40px' }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px',
                  padding: '0',
                  color: '#6c757d'
                }}
              >
                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            
            {passwords.confirmPassword && (
              <small style={{ 
                color: passwords.newPassword === passwords.confirmPassword ? '#198754' : '#dc3545',
                fontSize: '12px',
                display: 'block',
                marginTop: '6px'
              }}>
                {passwords.newPassword === passwords.confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
              </small>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-success"
            disabled={loading}
            style={{ width: '100%', marginTop: '8px' }}
          >
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <Link to="/login" style={{ color: '#0d6efd', textDecoration: 'none', fontSize: '14px' }}>
            ← Back to Login
          </Link>
        </div>

        <div style={{ 
          marginTop: '24px', 
          padding: '12px', 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeaa7', 
          borderRadius: '6px',
          fontSize: '13px',
          color: '#856404'
        }}>
          <strong>🔒 Security Tip:</strong> Use a unique password that you don't use on other websites.
          Consider using a password manager to generate and store strong passwords.
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
